/**
 * Inline AI mascot — 작은 원형 버튼 하나가 어시스턴트 전체를 컨트롤.
 * 클릭하면 활성(🤖) / 비활성(💤) 토글. 활성 시 말풍선은 마스코트 바로
 * 위에 절대 위치로 떠서 HeroPanel 상단 경계를 넘어 보임.
 *
 * 배치는 부모가 결정 — 이 컴포넌트는 `relative`만 깔고 자기 안에서
 * 마스코트 + 말풍선을 조립하니, HeroPanel · 헤더 등 어디든 인라인으로
 * 삽입 가능.
 */
import { SpeechBubble } from '@/components/bubble/SpeechBubble';
import { useAssistantStore } from '@/state/assistantStore';
import { useSettingsStore } from '@/state/settingsStore';

const VERDICT_CLASS: Record<'good' | 'okay' | 'mistake', string> = {
  good: 'text-success',
  okay: 'text-warning',
  mistake: 'text-danger',
};

export function AssistantPanel() {
  const mode = useSettingsStore((s) => s.assistantMode);
  const setMode = useSettingsStore((s) => s.setAssistantMode);
  const suggestions = useAssistantStore((s) => s.suggestions);
  const feedback = useAssistantStore((s) => s.feedback);

  const on = mode !== 'off';
  const toggle = () => setMode(on ? 'off' : 'both');

  const latestSuggestion = suggestions[suggestions.length - 1];
  const latestFeedback = feedback[feedback.length - 1];
  const showingFeedback =
    on && latestFeedback && latestFeedback.createdAt > (latestSuggestion?.createdAt ?? 0);
  const showingSuggestion =
    on && latestSuggestion && latestSuggestion.createdAt >= (latestFeedback?.createdAt ?? 0);
  const bubbleText = on
    ? showingFeedback && latestFeedback
      ? latestFeedback.text
      : showingSuggestion && latestSuggestion
        ? latestSuggestion.text
        : '준비 완료. 당신의 차례를 지켜봐요.'
    : null;
  const bubbleTone = showingFeedback && latestFeedback ? VERDICT_CLASS[latestFeedback.verdict] : '';
  const bubbleTestId = showingFeedback ? 'assistant-feedback' : 'assistant-suggestion';

  return (
    <div
      className="relative inline-flex flex-col items-center"
      data-testid="assistant-panel"
      data-mode={mode}
    >
      {bubbleText ? (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-20 animate-[reveal-up_0.35s_ease-out_both]">
          <SpeechBubble
            text={bubbleText}
            tail="left"
            charDelayMs={28}
            className={bubbleTone}
            testId={bubbleTestId}
          />
        </div>
      ) : null}
      <button
        type="button"
        onClick={toggle}
        className={`relative w-14 h-14 rounded-pill flex items-center justify-center border-2 transition-all active:translate-y-0.5 shadow-[3px_3px_0_0_hsl(var(--color-bg-deeper))] ${
          on
            ? 'border-accent-alt bg-accent-alt/20 text-accent-alt glow-cyan idle-breathe'
            : 'border-border bg-bg-elev text-muted/70 hover:text-fg hover:border-accent-alt/50'
        }`}
        aria-pressed={on}
        aria-label={on ? '어시스턴트 끄기' : '어시스턴트 켜기'}
        data-testid="assistant-mode"
        data-mode={mode}
      >
        <span className="text-3xl leading-none" aria-hidden="true">
          {on ? '🤖' : '💤'}
        </span>
      </button>
    </div>
  );
}
